import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Landmark, Receipt, Download, ExternalLink, AlertTriangle, ShieldCheck, Percent, FileText } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import StatCard from "./StatCard";

const STATUS_BADGE = {
  calculated: { label: "Calculated", cls: "bg-teal-500/15 text-teal-600 dark:text-teal-400" },
  zero_tax_jurisdiction: { label: "Zero Tax", cls: "bg-sky-500/15 text-sky-600 dark:text-sky-400" },
  manual_override: { label: "Override", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  manual_rate: { label: "Manual Rate", cls: "bg-slate-500/15 text-slate-500 dark:text-slate-400" },
  tax_exempt: { label: "Exempt", cls: "bg-purple-500/15 text-purple-600 dark:text-purple-400" },
  needs_address: { label: "Needs Address", cls: "bg-orange-500/15 text-orange-600 dark:text-orange-400" },
  calculation_failed: { label: "Failed", cls: "bg-red-500/15 text-red-600 dark:text-red-400" },
};

const StatusChip = ({ status }) => {
  const meta = STATUS_BADGE[status];
  if (!meta) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

const JobRows = ({ jobs, formatCurrency, formatDate, linkColor, emptyText, showNote = false }) => (
  jobs.length === 0 ? (
    <TableRow className="border-theme">
      <TableCell colSpan={showNote ? 9 : 8} className="text-center text-muted-foreground py-8">{emptyText}</TableCell>
    </TableRow>
  ) : (
    jobs.map((j, i) => (
      <TableRow key={j.job_id || i} className="border-theme hover:bg-theme-surface">
        <TableCell>
          <Link to={`/dashboard/jobs?job=${j.job_id}`} className={`${linkColor} flex items-center gap-1 font-mono text-xs`} data-testid={`tax-job-link-${j.job_id}`}>
            {j.job_display_id || j.job_id?.slice(-8) || "—"}<ExternalLink className="w-3 h-3" />
          </Link>
        </TableCell>
        <TableCell className="text-muted-foreground text-sm whitespace-nowrap">{formatDate(j.date)}</TableCell>
        <TableCell className="text-foreground font-medium max-w-[140px] truncate">{j.customer_name || "—"}</TableCell>
        <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">{j.jurisdiction || (showNote ? (j.note || "—") : "—")}</TableCell>
        <TableCell><StatusChip status={j.status} /></TableCell>
        <TableCell className="text-right text-foreground">{formatCurrency(j.pre_tax_total)}</TableCell>
        <TableCell className="text-right text-muted-foreground">{j.tax_rate ? `${j.tax_rate}%` : "—"}</TableCell>
        <TableCell className="text-right text-teal-600 dark:text-teal-400 font-medium">{formatCurrency(j.tax_amount)}</TableCell>
        <TableCell className="text-right text-green-600">{formatCurrency(j.tax_collected)}</TableCell>
      </TableRow>
    ))
  )
);

const TaxReportTab = ({ taxData, formatCurrency, formatDate, exportToCSV, exportToPDF, exportingPdf }) => {
  const { isDark } = useTheme();
  if (!taxData) return null;

  const { summary, jurisdictions, components, jobs, exempt, attention } = taxData;
  const linkColor = isDark ? "text-[#FFCC00] hover:text-[#FFCC00]/80" : "text-blue-600 hover:text-blue-800";
  const btnOutline = isDark ? "border-white/20 text-foreground hover:bg-theme-hover" : "border-gray-300 text-gray-700 hover:bg-gray-100";

  const exportRows = jobs.map(j => ({
    job_id: j.job_display_id || j.job_id,
    date: j.date,
    customer: j.customer_name,
    jurisdiction: j.jurisdiction,
    status: j.status,
    taxable_amount: j.pre_tax_total,
    tax_rate_pct: j.tax_rate,
    tax_charged: j.tax_amount,
    tax_collected: j.tax_collected,
  }));

  return (
    <div className="space-y-6" data-testid="tax-report-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatCard title="Tax Charged" value={formatCurrency(summary.total_tax_charged)} icon={Receipt} color="text-teal-600" bgColor="bg-teal-500/10" data-testid="tax-charged-card" />
        <StatCard title="Tax Collected" value={formatCurrency(summary.total_tax_collected)} icon={Receipt} color="text-green-600" bgColor="bg-green-500/10" data-testid="tax-collected-card" />
        <StatCard title="Tax Outstanding" value={formatCurrency(summary.total_tax_outstanding)} icon={Receipt} color="text-orange-500" bgColor="bg-orange-500/10" data-testid="tax-outstanding-card" />
        <StatCard title="Taxable Sales" value={formatCurrency(summary.taxable_sales)} icon={Landmark} color="text-blue-500" bgColor="bg-blue-500/10" data-testid="taxable-sales-card" />
        <StatCard title="Taxed Jobs" value={summary.taxed_jobs} icon={Percent} color="text-cyan-600" bgColor="bg-cyan-500/10" data-testid="taxed-jobs-card" />
        <StatCard title="Exempt Jobs" value={summary.exempt_jobs} icon={ShieldCheck} color="text-purple-500" bgColor="bg-purple-500/10" data-testid="exempt-jobs-card" />
      </div>

      {attention.length > 0 && (
        <Card className="glass-card border-orange-500/40" data-testid="tax-attention-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-foreground text-base font-['Manrope'] flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              Needs Attention — tax could not be calculated ({attention.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">
              These completed jobs are missing a full service address or the tax lookup failed. Fix the address on the job (or apply a manual override) so tax gets charged — they are NOT counted in the totals above.
            </p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-theme hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Job</TableHead>
                    <TableHead className="text-muted-foreground">Date</TableHead>
                    <TableHead className="text-muted-foreground">Customer</TableHead>
                    <TableHead className="text-muted-foreground">Issue</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground text-right">Amount</TableHead>
                    <TableHead className="text-muted-foreground text-right">Rate</TableHead>
                    <TableHead className="text-muted-foreground text-right">Tax</TableHead>
                    <TableHead className="text-muted-foreground text-right">Collected</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <JobRows jobs={attention} formatCurrency={formatCurrency} formatDate={formatDate} linkColor={linkColor} emptyText="" showNote />
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="glass-card border-theme" data-testid="tax-jurisdictions-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base font-['Manrope'] flex items-center gap-2">
            <Landmark className="w-4 h-4 text-teal-600" />
            Tax by Jurisdiction
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-theme hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Jurisdiction</TableHead>
                  <TableHead className="text-muted-foreground text-right">Jobs</TableHead>
                  <TableHead className="text-muted-foreground text-right">Taxable Sales</TableHead>
                  <TableHead className="text-muted-foreground text-right">Rate(s)</TableHead>
                  <TableHead className="text-muted-foreground text-right">Tax Charged</TableHead>
                  <TableHead className="text-muted-foreground text-right">Tax Collected</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jurisdictions.length === 0 ? (
                  <TableRow className="border-theme">
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">No tax charged in the selected period</TableCell>
                  </TableRow>
                ) : (
                  jurisdictions.map((j, i) => (
                    <TableRow key={i} className="border-theme hover:bg-theme-surface" data-testid={`tax-juris-row-${i}`}>
                      <TableCell className="text-foreground font-medium">{j.jurisdiction}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{j.jobs}</TableCell>
                      <TableCell className="text-right text-foreground">{formatCurrency(j.taxable_sales)}</TableCell>
                      <TableCell className="text-right text-muted-foreground text-sm">{(j.rates || []).map(r => `${r}%`).join(", ") || "—"}</TableCell>
                      <TableCell className="text-right text-teal-600 dark:text-teal-400 font-semibold">{formatCurrency(j.tax_charged)}</TableCell>
                      <TableCell className="text-right text-green-600 font-medium">{formatCurrency(j.tax_collected)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {components.length > 0 && (
            <div className="mt-4 pt-4 border-t border-theme">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Component Breakdown</p>
              <div className="flex flex-wrap gap-3">
                {components.map((c, i) => (
                  <div key={i} className="bg-theme-surface rounded-lg px-4 py-2 border border-theme" data-testid={`tax-component-${i}`}>
                    <p className="text-foreground font-medium text-sm">{c.name}{c.rate ? ` (${c.rate}%)` : ""}</p>
                    <p className="text-teal-600 dark:text-teal-400 font-semibold">{formatCurrency(c.amount)}</p>
                    <p className="text-muted-foreground text-xs">{c.jobs} job{c.jobs === 1 ? "" : "s"}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="glass-card border-theme" data-testid="tax-jobs-card">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-foreground text-base font-['Manrope']">Taxed Jobs</CardTitle>
          <div className="flex items-center gap-2">
            {exportToPDF && (
              <Button variant="outline" size="sm" onClick={() => exportToPDF("tax")} disabled={exportingPdf === "tax"} className={btnOutline} data-testid="export-tax-pdf-btn">
                <FileText className="w-4 h-4 mr-2" /> {exportingPdf === "tax" ? "Exporting…" : "Export PDF"}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => exportToCSV(exportRows, "tax_report")} className={btnOutline} data-testid="export-tax-btn">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-theme hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Job</TableHead>
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground">Customer</TableHead>
                  <TableHead className="text-muted-foreground">Jurisdiction</TableHead>
                  <TableHead className="text-muted-foreground">Status</TableHead>
                  <TableHead className="text-muted-foreground text-right">Taxable</TableHead>
                  <TableHead className="text-muted-foreground text-right">Rate</TableHead>
                  <TableHead className="text-muted-foreground text-right">Tax</TableHead>
                  <TableHead className="text-muted-foreground text-right">Collected</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <JobRows jobs={jobs} formatCurrency={formatCurrency} formatDate={formatDate} linkColor={linkColor} emptyText="No taxed jobs in the selected period" />
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {exempt.length > 0 && (
        <Card className="glass-card border-theme" data-testid="tax-exempt-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-foreground text-base font-['Manrope'] flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-purple-500" />
              Tax-Exempt Jobs ({exempt.length} — {formatCurrency(summary.exempt_sales)} in exempt sales)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-theme hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Job</TableHead>
                    <TableHead className="text-muted-foreground">Date</TableHead>
                    <TableHead className="text-muted-foreground">Customer</TableHead>
                    <TableHead className="text-muted-foreground">Jurisdiction</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground text-right">Sales</TableHead>
                    <TableHead className="text-muted-foreground text-right">Rate</TableHead>
                    <TableHead className="text-muted-foreground text-right">Tax</TableHead>
                    <TableHead className="text-muted-foreground text-right">Collected</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <JobRows jobs={exempt} formatCurrency={formatCurrency} formatDate={formatDate} linkColor={linkColor} emptyText="" />
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TaxReportTab;
